import React, { Component } from "react";
import { Container, Row, Col, Form } from "react-bootstrap"
import { TableCell, TableRow } from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import Select from '../../../Components/PCSelect';
import WeekRangeSelect from '../../../Components/WeekRangeSelect';
import PhoneClinicGrid from "../../../Components/PhoneClinicGrid";
import RowActions from "../../../Components/PhoneClinicGrid/RowActions";
import StatusChip from '../shared/StatusChip';
import { approvalStatus, approvalIsEditable, approvalActions, hasAction, isSuperAdmin } from '../../../helper';
import BulkActionBar from '../../../Components/PhoneClinicGrid/BulkActionBar';
import { runTransition } from '../shared/approvalApi';
import FormCard from '../shared/FormCard';

/* Generic grid + form for the small setup masters.
 *
 * Several setup screens are structurally identical inline-edit grids, so they share this
 * component and differ only by the config each thin page passes in. Backed by the matching thin
 * endpoints over helper/SetupCrud.php, so the shape is uniform on both sides.
 *
 * STILL IN USE by Menu Setup, Roles and Shop Transfer. It was originally written for the five
 * geography/currency screens, which were PARKED on 2026-10-05 — those pages are dormant on disk
 * and still import this component, so do not remove it.
 *
 * config:
 *   title        grid heading, e.g. "MENU SETUP"
 *   addLabel     button text, e.g. "Add Menu"
 *   pk           primary-key field name
 *   endpoints    { list, save, del }  paths under REACT_APP_BASE_URL
 *   lookups      [{ stateKey, endpoint, valueField, labelField }]  parent dropdown sources
 *   fields       [{ name, label, type: text|number|select|week, required, lookup, options,
 *                  initial, md, step }]  — `options` is a fixed [{value,label}] list, used
 *                  instead of `lookup`; `initial` seeds a new record's value
 *   columns      [{ label, field, align }]  grid columns
 *   labelField   optional: which field names a row in the delete prompt (defaults to fields[0])
 *   deleteWarning  optional: extra sentence shown in the delete prompt, for entities where
 *                  deactivating is the safer option
 *   approval     optional: turns on the approve / request workflow for this entity —
 *                  { statusField, endpoint, menuUrl, columnLabel }
 *                  Adds a status column plus Send-for-approval / Approve / Reject / Reopen
 *                  buttons, and disables Edit and Delete on anything that is not PENDING.
 *                  Omit it and the screen behaves exactly as before.
 */

class SetupMaster extends Component {
    constructor(props) {
        super(props);
        const form = {};
        props.config.fields.forEach(f => { form[f.name] = f.initial === undefined ? "" : f.initial });
        form[props.config.pk] = "";

        this.state = {
            rows: [],
            showForm: false,
            form,
            lookupData: {},
            // Only whether to RENDER Approve / Reject / Reopen. Every transition is re-checked
            // server-side against pc_role_rights, so this is an affordance, not a gate.
            canApprove: props.config.approval
                ? hasAction(props.config.approval.menuUrl, 'APPROVE')
                : false,
            // Super Admin may edit or delete at any status; the record keeps that status.
            // Re-checked server-side by pc_approval_lock_check() from the signed token.
            canOverride: isSuperAdmin(),
            // Bulk selection. Ids as strings, so they compare cleanly against data-pcid.
            selectedIds: [],
            bulkBusy: false
        };
        this.gridRef = React.createRef(null);
    }

    componentDidMount() {
        this.loadLookups()
    }

    // Parent dropdowns come from the same list endpoints the grids use, so there is one source
    // of truth per entity rather than a parallel lookup endpoint.
    async loadLookups() {
        const { lookups = [] } = this.props.config;
        const lookupData = {};
        for (const lk of lookups) {
            try {
                // Most lookups are the Setups list endpoints (POST, paginated), but some of the
                // older shared lookups — PCGetShops.php for one — only accept GET.
                const res = lk.method === 'get'
                    ? await axios.get(`${process.env.REACT_APP_BASE_URL}${lk.endpoint}`)
                    : await axios.post(`${process.env.REACT_APP_BASE_URL}${lk.endpoint}`, {
                        totalRecordsPerPage: 1000,
                        recordOffset: 1
                    });
                lookupData[lk.stateKey] = (res.data.success === 1 ? res.data.data : []).map(r => ({
                    value: r[lk.valueField],
                    label: r[lk.labelField]
                }));
            } catch (error) {
                console.error(`lookup ${lk.stateKey} failed :>> `, error);
                lookupData[lk.stateKey] = [];
            }
        }
        this.setState({ lookupData })
    }

    async getList(searchData = [], pagination = { recordOffset: 1, pageSize: 50 }) {
        const { config } = this.props;
        this.props.toggleLoader && this.props.toggleLoader(true)
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}${config.endpoints.list}`, {
                searchData,
                totalRecordsPerPage: pagination.pageSize,
                recordOffset: pagination.recordOffset
            });
            const resp = res.data
            let rows = []
            if (resp.success === 1) {
                rows = resp.data
                pagination.totalItems = parseInt(resp.Page_count[0]['TOTAL_PAGE_COUNT'])
                pagination.totalPages = Math.ceil(pagination.totalItems / (pagination.pageSize || 50))
                this.gridRef.current?.setPaginationData(pagination)
            }
            this.setState({ rows })
        } catch (error) {
            console.error('getList error :>> ', error);
            this.setState({ rows: [] })
        }
        this.props.toggleLoader && this.props.toggleLoader(false)
    }

    resetForm() {
        const { config } = this.props;
        const form = {};
        config.fields.forEach(f => { form[f.name] = f.initial === undefined ? "" : f.initial });
        form[config.pk] = "";
        this.setState({ showForm: false, form })
    }

    setField(name, value) {
        this.setState(prev => ({ form: { ...prev.form, [name]: value } }))
    }

    async save() {
        const { config } = this.props;
        const { form } = this.state;

        // Validated here too so a required field doesn't cost a round-trip; the endpoint
        // enforces the same rules and stays the authority.
        for (const f of config.fields) {
            if (f.required && String(form[f.name] ?? "").trim() === "") {
                Swal.fire('Error', `Please Fill ${f.label}`, 'error')
                return
            }
        }

        this.props.toggleLoader && this.props.toggleLoader(true)
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}${config.endpoints.save}`, form);
            if (res.data.success === 1) {
                this.resetForm()
                this.getList()
                this.loadLookups()   // a new parent must appear in the child screens' pickers
            } else {
                Swal.fire('Error', res.data.message || 'Could not save the record.', 'error')
            }
        } catch (error) {
            console.error('save error :>> ', error);
            Swal.fire('Error', 'Could not save the record.', 'error')
        }
        this.props.toggleLoader && this.props.toggleLoader(false)
    }

    /* With no `approval` config every row stays editable, so existing screens are unaffected. */
    rowEditable(row) {
        const { approval } = this.props.config;
        if (!approval) return true;
        return approvalIsEditable(row[approval.statusField], this.state.canOverride);
    }

    /* True when this row is only editable BECAUSE the user is Super Admin — used to explain in
     * the tooltip that the record is approved and will stay approved. */
    rowIsOverride(row) {
        const { approval } = this.props.config;
        if (!approval) return false;
        return this.state.canOverride && !approvalIsEditable(row[approval.statusField]);
    }

    /* Approval transition: request / approve / reject / reopen. The endpoint decides whether
     * this user may make the move and returns the message shown either way. */
    async transition(row, to) {
        const { approval, pk, labelField, fields } = this.props.config;
        const label = row[labelField || fields[0].name];
        const action = approvalActions(row[approval.statusField], true).find(a => a.to === to);

        const confirm = await Swal.fire({
            title: `${action ? action.label : 'Update'}?`,
            text: `${label} will be marked ${to.toLowerCase()}.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        });
        if (!confirm.isConfirmed) return;

        /* Routed through the SAME endpoint and helper the bulk bar uses, with a list of one.
         * Keeping one code path means a per-row button and a bulk button can never disagree
         * about what a transition does. */
        const result = await runTransition(approval.module, to, [row[pk]]);
        if (result && result.approved > 0 && this.gridRef.current) {
            this.gridRef.current.refresh();
        }
    }

    /* Bulk: act on every selected row the chosen transition applies to. */
    async bulkTransition(to, ids) {
        const { approval } = this.props.config;
        const action = approvalActions(to === 'PENDING' ? 'APPROVED' : 'PENDING', true).find(a => a.to === to);
        const confirm = await Swal.fire({
            title: `${action ? action.label : 'Update'} ${ids.length} record${ids.length === 1 ? '' : 's'}?`,
            text: `They will be marked ${to.toLowerCase()}.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        });
        if (!confirm.isConfirmed) return;

        this.setState({ bulkBusy: true });
        const result = await runTransition(approval.module, to, ids);
        this.setState({ bulkBusy: false, selectedIds: [] });
        if (result && result.approved > 0 && this.gridRef.current) {
            this.gridRef.current.refresh();
        }
    }

    edit(idx) {
        const { config } = this.props;
        const row = this.state.rows[idx];
        const form = {};
        config.fields.forEach(f => {
            const v = row[f.name];
            form[f.name] = (v === null || v === undefined) ? "" : String(v)
        });
        form[config.pk] = row[config.pk];
        this.setState({ form, showForm: true })
    }

    async remove(idx) {
        const { config } = this.props;
        const row = this.state.rows[idx];
        const label = row[config.labelField || config.fields[0].name];

        const confirm = await Swal.fire({
            title: `Delete ${config.entity.toLowerCase()}?`,
            text: `${label} will be removed.${config.deleteWarning ? ' ' + config.deleteWarning : ''}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            confirmButtonColor: '#a32f2f'
        })
        if (!confirm.isConfirmed) return

        this.props.toggleLoader && this.props.toggleLoader(true)
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}${config.endpoints.del}`, {
                [config.pk]: row[config.pk]
            });
            if (res.data.success === 1) {
                this.getList()
                this.loadLookups()
            } else {
                // The endpoint refuses to orphan children and says how many depend on this
                // record — show that verbatim instead of a generic failure.
                Swal.fire('Cannot delete', res.data.message || 'Could not delete the record.', 'info')
            }
        } catch (error) {
            console.error('remove error :>> ', error);
            Swal.fire('Error', 'Could not delete the record.', 'error')
        }
        this.props.toggleLoader && this.props.toggleLoader(false)
    }

    renderInput(f) {
        const { form, lookupData } = this.state;

        // Week codes ('YYYY-Www') use the app's shared week dropdown, so a transfer window is
        // picked the same way as a week anywhere else rather than typed by hand.
        if (f.type === 'week') {
            return (
                <WeekRangeSelect
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    value={form[f.name]}
                    onChange={(e) => this.setField(f.name, e.target.value)}
                />
            )
        }

        if (f.type === 'select') {
            // A fixed list (Active / Inactive, say) is declared inline; anything sourced from
            // another table still goes through `lookups`.
            const options = f.options || lookupData[f.lookup] || [];
            const current = options.find(o => String(o.value) === String(form[f.name])) || "";
            return (
                <Select menuPortalTarget={document.body}
                    menuPlacement="auto"
                    isClearable={true}
                    value={current}
                    options={options}
                    onChange={(opt) => this.setField(f.name, opt ? opt.value : "")}
                />
            )
        }

        return (
            <Form.Control
                type={f.type === 'number' ? 'number' : 'text'}
                step={f.step}
                value={form[f.name]}
                name={f.name}
                onChange={(e) => this.setField(f.name, e.target.value)}
            />
        )
    }

    render() {
        const { config } = this.props;
        const { rows, showForm, form, selectedIds, bulkBusy } = this.state;

        /* Selection only exists on screens that declare `approval` — there is nothing to bulk
           approve otherwise, and the other setup screens must keep their current grid exactly. */
        const bulk = config.approval ? {
            rowIds: rows.map(r => String(r[config.pk])),
            // A row is selectable only if some transition is actually available to this user.
            selectableIds: rows
                .filter(r => approvalActions(r[config.approval.statusField], this.state.canApprove).length > 0)
                .map(r => String(r[config.pk])),
            selected: selectedIds,
            onChange: (ids) => this.setState({ selectedIds: ids }),
        } : undefined;

        const selectedRows = config.approval
            ? rows.filter(r => selectedIds.includes(String(r[config.pk])))
            : [];

        return (
            <Container>
                {showForm === false ?
                    <>
                    {config.approval && (
                        <BulkActionBar
                            rows={selectedRows}
                            statusField={config.approval.statusField}
                            idField={config.pk}
                            canApprove={this.state.canApprove}
                            busy={bulkBusy}
                            onAction={this.bulkTransition.bind(this)}
                            onClear={() => this.setState({ selectedIds: [] })}
                        />
                    )}
                    <PhoneClinicGrid
                        selection={bulk}
                        title={config.title}
                        buttonLabel={config.addLabel}
                        getData={this.getList.bind(this)}
                        onClickButton={() => this.setState({ showForm: true })}
                        isEditable={true}
                        ref={this.gridRef}
                        tableHeadData={[
                            ...config.columns.map(c => ({ label: c.label, isHidden: false })),
                            ...(config.approval
                                ? [{ label: config.approval.columnLabel || 'APPROVAL', isHidden: false }]
                                : []),
                        ]}
                    >
                        {rows.map((row, idx) => (
                            <TableRow
                                key={idx}
                                /* The grid reads this to know which record a checkbox selects. */
                                data-pcid={String(row[config.pk])}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {config.columns.map((c, ci) => (
                                    <TableCell key={ci} align={c.align || 'left'}>{row[c.field]}</TableCell>
                                ))}
                                {config.approval && (
                                    <TableCell align='left'>
                                        <StatusChip label={approvalStatus(row[config.approval.statusField])} />
                                    </TableCell>
                                )}
                                <TableCell align='center'>
                                    {/* With approval on, only a PENDING row may be edited or deleted.
                                        The endpoints refuse as well; disabling just avoids a 409. The tooltip says why. */}
                                    <RowActions actions={[
                                        {
                                            type: 'edit', onClick: this.edit.bind(this, idx),
                                            disabled: !this.rowEditable(row),
                                            title: this.rowIsOverride(row)
                                                ? 'This record is approved. As Super Admin you can edit it, and it will stay approved — it is not sent back for approval.'
                                                : this.rowEditable(row) ? '' : 'Reopen this record to pending before editing it',
                                        },
                                        ...(config.approval ? approvalActions(row[config.approval.statusField], this.state.canApprove).map(act => ({
                                            type: act.type, title: act.label, onClick: this.transition.bind(this, row, act.to),
                                        })) : []),
                                        {
                                            type: 'delete', onClick: this.remove.bind(this, idx),
                                            disabled: !this.rowEditable(row),
                                            title: this.rowIsOverride(row)
                                                ? 'This record is approved. As Super Admin you can delete it.'
                                                : this.rowEditable(row) ? '' : 'Reopen this record to pending before deleting it',
                                        },
                                    ]} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </PhoneClinicGrid>
                    </>
                    :
                    <>
                        <FormCard title={form[config.pk] === "" ? config.addLabel : `Edit ${config.entity}`} mode={form[config.pk] === "" ? 'new' : 'edit'}
                            onCancel={this.resetForm.bind(this)}
                            onSave={this.save.bind(this)} saveLabel="Save">
                        <Row>
                            {config.fields.map((f, i) => (
                                <Col md={f.md || 4} key={i}>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>
                                            {f.label}
                                            {f.required && <span style={{ color: '#a32f2f' }} > *</span>}
                                        </Form.Label>
                                        {this.renderInput(f)}
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>
                        </FormCard>
                    </>
                }
            </Container>
        );
    }
}

export default SetupMaster;
